import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { ConfigService } from './config.service';
import { MuxCredentialService } from './mux-credential.service';
import { IdentityService } from './identity.service';

/**
 * The user's MODEL POOL — the derived noun from the 2026-08-13 design
 * session: credentials unlock models; agents and channel overrides pick a
 * (model, credential) tuple, rendered as "Model · credential" with the
 * credential as a subtle property of the row.
 *
 * Pool entries carry everything a picker needs to write an agent config
 * (backend + model + credentials_id) or a channel override ("backend:model")
 * in one gesture.
 */
@Injectable({ providedIn: 'root' })
export class ModelPoolService {
  constructor(private http: HttpClient, private credService: MuxCredentialService, private identity: IdentityService) {}

  private _cache: ModelPoolService.PoolEntry[] | null = null;
  private _cacheTeam: string | null = null;   // the pool is team-scoped: policy filters it
  /** The team policy summary that came with the last `/models?team=` (null = no policy). */
  public lastPolicy: any = null;
  /** Backends the last `/models?team=` returned — under a policy, only the allowed ones. */
  public lastBackends: string[] = [];

  /** Whether any backend of this vendor is allowed by the policy (provider switched on). */
  vendorAllowed(vendor: string | null | undefined): boolean {
    if (!this.lastPolicy?.exists || !vendor) return true;
    return ['subscription', 'api_key'].some((mode) =>
      (ModelPoolService.BACKENDS_BY_VENDOR_MODE[`${vendor}:${mode}`] || []).some((b) => this.lastBackends.includes(b)));
  }

  /** Whether the user may bill to this credential under the last-loaded policy:
   *  vendors with slots are limited to the slots the user's groups allow. */
  credentialAllowed(c: { id?: string | null; vendor?: string | null }): boolean {
    const policy = this.lastPolicy;
    if (!policy?.exists) return true;
    if (!this.vendorAllowed(c.vendor)) return false;       // provider switched off → its credentials are moot
    const slots: Record<string, { vendor: string }> = policy.slots || {};
    const vendorHasSlots = Object.values(slots).some((sl) => sl.vendor === c.vendor);
    if (!vendorHasSlots) return true;                       // no slots for this vendor → as before
    const allowed: string[] | null = policy.slots_allowed ?? null;
    return !!c.id && c.id in slots && (allowed === null || allowed.includes(c.id));
  }
  /** Filter a credential list to what the policy lets this user use (pool must be loaded). */
  allowedCredentials<T extends { id?: string | null; vendor?: string | null }>(creds: T[]): T[] {
    return creds.filter((c) => this.credentialAllowed(c));
  }

  /** Preferred backend per (vendor, mode), first with a live catalog wins.
   *  MUST mirror _BACKEND_BY_VENDOR_MODE in lit-lib services/credentials.py —
   *  the authority: a credential's MODE picks the backend, not just its vendor
   *  (google api_key=gemini vs subscription=antigravity; claude-interactive is
   *  not valid for anthropic per-token). */
  private static BACKENDS_BY_VENDOR_MODE: Record<string, string[]> = {
    'anthropic:subscription': ['claude-interactive', 'claude-cli'],
    'anthropic:api_key': ['claude-cli', 'claude'],
    'google:api_key': ['gemini'],
    'google:subscription': ['antigravity'],
    'openai:api_key': ['chatgpt'],
    'openai:subscription': ['chatgpt'],
  };

  static modeLabel(mode: string): string {
    return mode === 'subscription' ? 'subscription'
      : mode === 'api_key' ? 'per-token' : mode;
  }

  static providerLabel(backendId: string): string {
    const map: Record<string, string> = {
      'claude-cli': 'Claude', 'claude-interactive': 'Claude',
      'claude': 'Claude API', 'codex': 'Codex', 'chatgpt': 'ChatGPT',
      'gemini-cli': 'Gemini', 'gemini': 'Gemini',
      'antigravity': 'Antigravity', 'ollama': 'Ollama (local)',
    };
    return map[backendId] || backendId;
  }

  /** The full pool: every (model, credential) tuple the user's credentials
   *  unlock, plus local Ollama models (no credential needed). */
  async pool(force = false, teamOverride?: string): Promise<ModelPoolService.PoolEntry[]> {
    // Team policy (docs/plans/org-policy.md): `/models?team=` is filtered
    // server-side to what THIS user may use in THIS team, and the response
    // carries the policy summary so credentials can be filtered to the slots
    // the user may bill to. The pool is therefore team-scoped.
    const team = teamOverride ?? (this.identity.me?.selectedTeamName || null);
    if (this._cache && !force && this._cacheTeam === team) return this._cache;
    const modelsUrl = `${ConfigService.muxUrl}/models` + (team ? `?team=${encodeURIComponent(team)}` : '');
    const [modelsResp, allCreds] = await Promise.all([
      lastValueFrom(this.http.get<any>(modelsUrl)),
      this.credService.list(team || undefined).catch(() => [] as MuxCredentialService.Credential[]),
    ]);
    this.lastPolicy = modelsResp?.policy || null;
    this.lastBackends = Object.keys(modelsResp?.models || {});
    const creds = this.allowedCredentials(allCreds);
    const catalogs: Record<string, { name: string; display_name: string }[]> =
      modelsResp?.models || {};
    const constraints: Record<string, string[]> = modelsResp?.constraints || {};

    // SORT ORDER (design session 2026-08-13): the model is the noun, the
    // credential its property — so iterate MODELS in curated catalog order
    // (capability/recency, not alphabet) and emit same-model tuples as
    // ADJACENT rows, subscription before per-token (the sensible default
    // payer leads). Providers in a fixed stable order; Ollama (local) last.
    const modeRank = (m: string | null) => (m === 'subscription' ? 0 : 1);
    const entries: ModelPoolService.PoolEntry[] = [];
    const vendorOrder = ['anthropic', 'openai', 'google'];
    const vendors = [...new Set(creds.map(c => c.vendor))]
      .sort((a, b) => {
        const ia = vendorOrder.indexOf(a), ib = vendorOrder.indexOf(b);
        return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
      });
    for (const vendor of vendors) {
      const vendorCreds = creds
        .filter(c => c.vendor === vendor)
        .sort((a, b) => modeRank(a.mode) - modeRank(b.mode) ||
                        (a.name || '').localeCompare(b.name || ''));
      // Mode picks the backend — bucket this vendor's credentials by the
      // backend their mode resolves to, keeping the credential sort order.
      const buckets = new Map<string, MuxCredentialService.Credential[]>();
      for (const cred of vendorCreds) {
        const prefs = ModelPoolService.BACKENDS_BY_VENDOR_MODE[`${vendor}:${cred.mode}`] || [];
        const backend = prefs.find(b => (catalogs[b] || []).length > 0);
        if (!backend) continue; // credential unlocks no reachable backend — honest omission
        if (!buckets.has(backend)) buckets.set(backend, []);
        buckets.get(backend)!.push(cred);
      }
      for (const [backend, bucketCreds] of buckets) {
        for (const m of catalogs[backend] || []) {
          for (const cred of bucketCreds) {
            const allow = constraints[`${cred.vendor}:${cred.mode}`];
            if (allow && !allow.includes(m.name)) continue;
            entries.push({
              backend,
              model: m.name,
              displayName: m.display_name || m.name,
              provider: ModelPoolService.providerLabel(backend),
              credentialId: cred.id,
              credentialName: cred.name || cred.id || 'default',
              credentialMode: cred.mode,
              overrideValue: `${backend}:${m.name}`,
              key: `${backend}|${m.name}|${cred.id || ''}`,
            });
          }
        }
      }
    }
    for (const m of catalogs['ollama'] || []) {
      entries.push({
        backend: 'ollama',
        model: m.name,
        displayName: m.display_name || m.name,
        provider: 'Ollama (local)',
        credentialId: null,
        credentialName: null,
        credentialMode: null,
        overrideValue: `ollama:${m.name}`,
        key: `ollama|${m.name}|`,
      });
    }
    this._cache = entries;
    this._cacheTeam = team;
    return entries;
  }

  /** Pool grouped by provider, preserving entry order. */
  async groups(force = false, teamOverride?: string): Promise<ModelPoolService.PoolGroup[]> {
    const entries = await this.pool(force, teamOverride);
    const byProvider = new Map<string, ModelPoolService.PoolEntry[]>();
    for (const e of entries) {
      if (!byProvider.has(e.provider)) byProvider.set(e.provider, []);
      byProvider.get(e.provider)!.push(e);
    }
    return Array.from(byProvider.entries()).map(([provider, models]) => ({ provider, models }));
  }

  invalidate(): void { this._cache = null; }
}

export namespace ModelPoolService {
  export interface PoolEntry {
    backend: string;
    model: string;
    displayName: string;
    provider: string;
    credentialId: string | null;   // null: flat default or no credential (ollama)
    credentialName: string | null; // null for ollama
    credentialMode: string | null;
    overrideValue: string;         // "backend:model" — the channel-override wire format
    key: string;                   // unique per (backend, model, credential) — the option value
  }
  export interface PoolGroup {
    provider: string;
    models: PoolEntry[];
  }
}
